// Catálogo inicial tomado del sitio público (4 oct 2026). Solo contenido visible para cualquier visitante.
// Precio de menores: el sitio no lo publica. Para la demo se usa una tarifa de EJEMPLO (75% del adulto),
// marcada así en pantalla, hasta tener la tarifa oficial.
// Esta ficha alimenta el seed de la base; en producción el catálogo se edita desde el panel.

export type Idioma = "es" | "en";
export type Texto = { es: string; en: string };

export type TourContenido = {
  idLegado: number;
  slug: string;
  tipo: "tour" | "combo";
  nombre: Texto;
  resumen: Texto;
  descripcion: Texto;
  incluye: { es: string[]; en: string[] };
  duracionMin: number | null;
  edadMinima: number;
  minimoPasajeros: number | null; // lugares pagados para que salga (boleto actual)
  regreso?: { lugar: { es: string; en: string }; minutos: number }; // dónde y cuánto después de salir pasa el camión de regreso (ejemplo)
  categoria: "camion" | "caballo" | "nocturno" | "aire" | "pueblo";
  adultoCentavos: number;
  ninoCentavos: number;
  precioNinoConfirmado: boolean;
  nota?: Texto;
  imagenes: { archivo: string; alt: Texto }[];
  videos?: { archivo: string; poster: string; alt: Texto }[]; // clips de stock (sin sonido) para la galería
  horarios: string[]; // horarios de prueba para la vista previa
  recurso: "camion" | "caballo" | "helicoptero";
};

const reglasComunes: { es: string[]; en: string[] } = {
  es: [
    "Llega al punto de check-in 15 minutos antes de tu salida.",
    "Todos los recorridos van con un guía capacitado.",
    "No se permite el ingreso de bebidas externas.",
    "El acceso con mascotas puede estar restringido según la experiencia.",
  ],
  en: [
    "Arrive at the check-in point 15 minutes before departure.",
    "Every tour is led by a trained guide.",
    "Outside drinks are not allowed.",
    "Pets may be restricted depending on the experience.",
  ],
};
export { reglasComunes };

export const queLlevar: Texto[] = [
  { es: "Ropa y calzado cómodo", en: "Comfortable clothes and shoes" },
  { es: "Gorra, lentes y protector solar", en: "Cap, sunglasses and sunscreen" },
  { es: "Cámara o binoculares", en: "Camera or binoculars" },
  { es: "Una mochila para tus cosas", en: "A small backpack" },
  { es: "Dinero extra", en: "Some extra cash" },
];

const img = (archivo: string, es: string, en: string) => ({ archivo: `/img/tours/${archivo}`, alt: { es, en } });

export const tours: TourContenido[] = [
  {
    idLegado: 1,
    slug: "sierra-del-tigre",
    tipo: "tour",
    nombre: { es: "Sierra del Tigre y Valle de Juárez", en: "Sierra del Tigre & Valle de Juárez" },
    resumen: {
      es: "Sube a lo más alto de la Sierra del Tigre, a 2,800 msnm, en camión tipo safari.",
      en: "Ride a safari truck to the top of the Sierra del Tigre, 2,800 m above sea level.",
    },
    descripcion: {
      es: "Recorrido por los miradores de la Suiza Mexicana, Barranca del Tecolote, la Antigua Ciénega y La Patricia, con parada en una tienda de rancho, la presa y el malecón del Valle de Juárez. Es un corredor natural de la Sierra Madre Occidental, con casi seis mil hectáreas de bosque.",
      en: "Visit the Suiza Mexicana, Barranca del Tecolote, Antigua Ciénega and La Patricia lookouts, with stops at a ranch store, the dam and the Valle de Juárez boardwalk. A natural corridor of the Sierra Madre Occidental with almost six thousand hectares of forest.",
    },
    incluye: {
      es: ["Paseo por la Sierra del Tigre", "Casa y tienda de rancho", "Visita a la presa", "Malecón en el Valle de Juárez"],
      en: ["Sierra del Tigre ride", "Ranch house and store", "Dam visit", "Valle de Juárez boardwalk"],
    },
    duracionMin: 120,
    edadMinima: 4,
    minimoPasajeros: 12,
    regreso: { lugar: { es: "Malecón del Valle de Juárez", en: "Valle de Juárez boardwalk" }, minutos: 75 },
    categoria: "camion",
    adultoCentavos: 60000,
    ninoCentavos: 40000,
    precioNinoConfirmado: true,
    imagenes: [
      img("st-img3.webp", "Camión safari en un camino de terracería de la sierra", "Safari truck on a dirt road in the sierra"),
      img("st-img1.webp", "Grupo de visitantes en un prado con niebla", "Group of visitors in a misty meadow"),
      img("st-img2.webp", "Vista del bosque desde dentro del camión", "Forest view from inside the truck"),
      img("pa-img1.webp", "Camino de la sierra", "Sierra road"),
      img("pa-img2.webp", "Camino de la sierra", "Sierra road"),
    ],
    videos: [{ archivo: "/media/v_bosque.mp4", poster: "/media/v_bosque.jpg", alt: { es: "Bosque de la sierra visto desde el aire", en: "Sierra forest from the air" } }],
    horarios: ["10:00", "12:00", "14:00", "16:00"],
    recurso: "camion",
  },
  {
    idLegado: 2,
    slug: "los-cazos-cascada",
    tipo: "tour",
    nombre: { es: "Los Cazos y Cascada El Salto", en: "Los Cazos & El Salto Waterfall" },
    resumen: {
      es: "Senderismo hasta la Cascada El Salto, el Jardín Encantado y las cabañas del pueblo.",
      en: "Hike to El Salto waterfall, the Enchanted Garden and the town's cabins.",
    },
    descripcion: {
      es: "Conoce la Cascada El Salto con una caminata por la naturaleza hacia el corazón de Mazamitla. Visitamos la Escalera de Cantera, el Jardín Encantado y las cabañas que hacen único al pueblo.",
      en: "Walk through nature to El Salto waterfall, in the heart of Mazamitla. We also visit the Stone Staircase, the Enchanted Garden and the cabins that make the town unique.",
    },
    incluye: {
      es: ["Paseo por Los Cazos", "Escalera de Cantera", "Jardín Encantado y cabañas", "Senderismo"],
      en: ["Los Cazos walk", "Stone Staircase", "Enchanted Garden and cabins", "Hiking"],
    },
    duracionMin: 150,
    edadMinima: 4,
    minimoPasajeros: 15,
    regreso: { lugar: { es: "Cascada El Salto", en: "El Salto waterfall" }, minutos: 120 },
    categoria: "camion",
    adultoCentavos: 65000,
    ninoCentavos: 55000,
    precioNinoConfirmado: true,
    imagenes: [
      img("lc-img2.webp", "Cascada El Salto entre rocas y bosque", "El Salto waterfall among rocks and forest"),
      img("lc-img1.webp", "Grupo con su guía en el bosque", "Group with their guide in the forest"),
      img("lc-img3.webp", "Visitantes junto al camión del tour", "Visitors next to the tour truck"),
      img("cz-img4.webp", "Cascada entre el bosque", "Waterfall in the forest"),
      img("cz-img5.webp", "Cascada entre el bosque", "Waterfall in the forest"),
      img("cz-img6.webp", "Cascada entre el bosque", "Waterfall in the forest"),
    ],
    videos: [{ archivo: "/media/v_cascada.mp4", poster: "/media/v_cascada.jpg", alt: { es: "Cascada en el bosque", en: "Waterfall in the forest" } }],
    horarios: ["11:00", "15:00"],
    recurso: "camion",
  },
  {
    idLegado: 3,
    slug: "nocturno-clasico",
    tipo: "tour",
    nombre: { es: "Nocturno Clásico Show", en: "Classic Night Show" },
    resumen: {
      es: "Vistas nocturnas, fogata con bombones, leyendas y música en el bosque.",
      en: "Night views, a marshmallow campfire, legends and music in the forest.",
    },
    descripcion: {
      es: "Recorrido por los alrededores del pueblo y el bosque. Descubre las vistas nocturnas de Mazamitla y disfruta una fogata asando bombones mientras escuchas leyendas, bailas y participas en dinámicas.",
      en: "A ride around town and into the forest. Enjoy Mazamitla's night views and a campfire with marshmallows while you hear legends, dance and play group games.",
    },
    incluye: {
      es: ["Paseo por el pueblo", "Entrada al bosque", "Fogata y senderismo nocturno"],
      en: ["Town ride", "Forest entry", "Campfire and night hike"],
    },
    duracionMin: 210,
    edadMinima: 4,
    minimoPasajeros: 12,
    categoria: "nocturno",
    adultoCentavos: 60000,
    ninoCentavos: 40000,
    precioNinoConfirmado: true,
    imagenes: [
      img("nc-img1.webp", "Fogata encendida de noche en el bosque", "Campfire at night in the forest"),
      img("nc-img2.webp", "Atardecer sobre las luces del pueblo", "Sunset over the town lights"),
      img("nc-img3.webp", "Grupo sentado alrededor de la fogata", "Group sitting around the campfire"),
      img("no-img1.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
      img("no-img2.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
      img("no-img3.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
    ],
    videos: [{ archivo: "/media/v_fogata.mp4", poster: "/media/v_fogata.jpg", alt: { es: "Fogata en la montaña", en: "Mountain campfire" } }],
    horarios: ["19:00"],
    recurso: "camion",
  },
  {
    idLegado: 4,
    slug: "fusion",
    tipo: "tour",
    nombre: { es: "Fusión Tour", en: "Fusion Tour" },
    resumen: {
      es: "Reserva natural con felinos y otras especies, y visita al Xtreme Park.",
      en: "Nature reserve with big cats and other species, plus Xtreme Park.",
    },
    descripcion: {
      es: "Recorrido familiar con visita guiada a la Reserva Natural, donde habitan felinos y una gran variedad de especies, y después al Xtreme Park, con cuatrimotos, paseos y tirolesa (actividades del parque con costo aparte). La entrada a Camino Real incluye puente colgante, aviario, túnel de cristal y cuarto de espejos.",
      en: "A family tour with a guided visit to the Nature Reserve, home to big cats and many other species, then Xtreme Park with ATVs, rides and zip line (park activities paid separately). Camino Real entry includes a suspension bridge, aviary, glass tunnel and mirror room.",
    },
    incluye: {
      es: ["Paseo en familia", "Visita guiada a la reserva de animales", "Visita al Xtreme Park"],
      en: ["Family ride", "Guided animal reserve visit", "Xtreme Park visit"],
    },
    duracionMin: 150,
    edadMinima: 4,
    minimoPasajeros: 12,
    regreso: { lugar: { es: "Xtreme Park", en: "Xtreme Park" }, minutos: 120 },
    categoria: "camion",
    adultoCentavos: 70000,
    ninoCentavos: 60000,
    precioNinoConfirmado: true,
    imagenes: [
      img("ft-img2.webp", "Mirador con escultura de alas sobre el bosque", "Lookout with a wings sculpture over the forest"),
      img("ft-img1.webp", "Visitante alimentando a un avestruz", "Visitor feeding an ostrich"),
      img("ft-img3.webp", "Entrada del parque con figuras de animales", "Park entrance with animal figures"),
      img("pa-img1.webp", "Camino de la sierra", "Sierra road"),
      img("cz-img4.webp", "Cascada entre el bosque", "Waterfall in the forest"),
      img("no-img1.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
    ],
    videos: [{ archivo: "/media/v_bosque.mp4", poster: "/media/v_bosque.jpg", alt: { es: "Bosque de la sierra visto desde el aire", en: "Sierra forest from the air" } }],
    horarios: ["10:30", "13:30"],
    recurso: "camion",
  },
  {
    idLegado: 5,
    slug: "caballo-1-hora",
    tipo: "tour",
    nombre: { es: "Paseo a caballo 1 hora", en: "1-hour horseback ride" },
    resumen: { es: "Cabalgata por el pueblo y el bosque.", en: "Ride through town and the forest." },
    descripcion: {
      es: "Pasea a caballo por el pueblo, con vistas del bosque y la tranquilidad de la sierra.",
      en: "Ride a horse through town, with forest views and the calm of the sierra.",
    },
    incluye: { es: ["Paseo a caballo", "Paisajes", "Vista al bosque"], en: ["Horseback ride", "Scenery", "Forest views"] },
    duracionMin: 60,
    edadMinima: 4,
    minimoPasajeros: 2,
    categoria: "caballo",
    adultoCentavos: 50000,
    ninoCentavos: 38000,
    precioNinoConfirmado: false,
    imagenes: [
      img("pc-img3.webp", "Grupo a caballo con su guía", "Group on horseback with their guide"),
      img("pc-img1.webp", "Visitante acariciando a un caballo", "Visitor petting a horse"),
      img("pc-img5.webp", "Cabalgata por un camino de tierra", "Horseback ride on a dirt road"),
      img("pc-img6.webp", "Paseo a caballo", "Horseback ride"),
      img("pc-img8.webp", "Paseo a caballo", "Horseback ride"),
      img("pc-img9.webp", "Paseo a caballo", "Horseback ride"),
    ],
    videos: [{ archivo: "/media/v_caballos.mp4", poster: "/media/v_caballos.jpg", alt: { es: "Cabalgata entre la niebla", en: "Horseback ride in the fog" } }],
    horarios: ["09:00", "11:00", "13:00", "15:00"],
    recurso: "caballo",
  },
  {
    idLegado: 6,
    slug: "caballo-2-horas",
    tipo: "tour",
    nombre: { es: "Paseo a caballo 2 horas", en: "2-hour horseback ride" },
    resumen: {
      es: "A caballo rumbo a la Sierra del Tigre: puente colgante de 50 m y laguna natural.",
      en: "Ride toward the Sierra del Tigre: a 50 m suspension bridge and a natural lake.",
    },
    descripcion: {
      es: "Cabalga rumbo a la Sierra del Tigre para conocer un puente colgante de 50 metros y una laguna natural, entre el canto de las aves.",
      en: "Ride toward the Sierra del Tigre to cross a 50-meter suspension bridge and reach a natural lake, surrounded by birdsong.",
    },
    incluye: { es: ["Paseo a caballo", "Laguna", "Puente colgante"], en: ["Horseback ride", "Lake", "Suspension bridge"] },
    duracionMin: 120,
    edadMinima: 4,
    minimoPasajeros: 2,
    categoria: "caballo",
    adultoCentavos: 65000,
    ninoCentavos: 49000,
    precioNinoConfirmado: false,
    imagenes: [
      img("pc-img5.webp", "Cabalgata por un camino de tierra", "Horseback ride on a dirt road"),
      img("pc-img3.webp", "Grupo a caballo con su guía", "Group on horseback with their guide"),
      img("pc-img8.webp", "Paseo a caballo", "Horseback ride"),
      img("pc-img9.webp", "Paseo a caballo", "Horseback ride"),
      img("pa-img1.webp", "Camino de la sierra", "Sierra road"),
    ],
    videos: [{ archivo: "/media/v_caballos.mp4", poster: "/media/v_caballos.jpg", alt: { es: "Cabalgata entre la niebla", en: "Horseback ride in the fog" } }],
    horarios: ["10:00", "14:00"],
    recurso: "caballo",
  },
  {
    idLegado: 7,
    slug: "caballo-cascada",
    tipo: "tour",
    nombre: { es: "A caballo a la cascada y Los Cazos", en: "Horseback to the waterfall & Los Cazos" },
    resumen: { es: "Senderos a caballo hasta una de las cascadas más impresionantes.", en: "Horse trails to one of the most striking waterfalls." },
    descripcion: {
      es: "Un recorrido a caballo por senderos y paisajes hasta una de las cascadas más impresionantes de Mazamitla.",
      en: "A horseback ride along trails to one of Mazamitla's most striking waterfalls.",
    },
    incluye: { es: ["Paseo a caballo", "Visita a la cascada", "Observación de la naturaleza"], en: ["Horseback ride", "Waterfall visit", "Nature watching"] },
    duracionMin: null,
    edadMinima: 12,
    minimoPasajeros: 2,
    categoria: "caballo",
    adultoCentavos: 75000,
    ninoCentavos: 56000,
    precioNinoConfirmado: false,
    imagenes: [
      img("pc-img1.webp", "Visitante acariciando a un caballo", "Visitor petting a horse"),
      img("cz-img4.webp", "Cascada entre el bosque", "Waterfall in the forest"),
      img("cz-img6.webp", "Cascada entre el bosque", "Waterfall in the forest"),
      img("pc-img9.webp", "Paseo a caballo", "Horseback ride"),
    ],
    videos: [{ archivo: "/media/v_caballos.mp4", poster: "/media/v_caballos.jpg", alt: { es: "Cabalgata entre la niebla", en: "Horseback ride in the fog" } }],
    horarios: ["10:00"],
    recurso: "caballo",
  },
  {
    idLegado: 14,
    slug: "nocturno-vip",
    tipo: "tour",
    nombre: { es: "Nocturno VIP", en: "VIP Night Tour" },
    resumen: {
      es: "Safari nocturno, Santuario de Epenche, fogata, leyendas y DJ.",
      en: "Night safari, Epenche Sanctuary, campfire, legends and a DJ.",
    },
    descripcion: {
      es: "Recorrido en camiones tipo safari, caminata nocturna por el bosque, acceso al Santuario de Epenche, fogata, leyendas, música con DJ, shots de bienvenida y estación de Glitter Bar.",
      en: "Safari truck ride, night forest walk, entry to the Epenche Sanctuary, campfire, legends, DJ music, welcome shots and a Glitter Bar station.",
    },
    incluye: {
      es: ["Camión tipo safari", "Caminata nocturna", "Santuario de Epenche", "Fogata y leyendas", "Música con DJ"],
      en: ["Safari truck", "Night walk", "Epenche Sanctuary", "Campfire and legends", "DJ music"],
    },
    duracionMin: null,
    edadMinima: 4,
    minimoPasajeros: 12,
    categoria: "nocturno",
    adultoCentavos: 150000,
    ninoCentavos: 90000,
    precioNinoConfirmado: true,
    imagenes: [
      img("mz-img1.webp", "Camión safari con la sierra al fondo", "Safari truck with the sierra behind"),
      img("mz-img2.webp", "Arco de madera con astas en la entrada", "Wooden arch with antlers at the entrance"),
      img("no-img1.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
      img("no-img2.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
      img("no-img3.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
    ],
    videos: [{ archivo: "/media/v_fogata.mp4", poster: "/media/v_fogata.jpg", alt: { es: "Fogata en la montaña", en: "Mountain campfire" } }],
    horarios: ["20:00"],
    recurso: "camion",
  },
  {
    idLegado: 15,
    slug: "helicoptero",
    tipo: "tour",
    nombre: { es: "Vuelo en helicóptero", en: "Helicopter flight" },
    resumen: {
      es: "15 minutos sobre la Sierra del Tigre, el Valle de Juárez y el Nevado de Colima.",
      en: "15 minutes over the Sierra del Tigre, Valle de Juárez and the Nevado de Colima.",
    },
    descripcion: {
      es: "Un vuelo exclusivo con vistas de la Sierra del Tigre, el Valle de Juárez y el Nevado de Colima. Precio por vuelo, hasta 5 pasajeros.",
      en: "An exclusive flight with views of the Sierra del Tigre, Valle de Juárez and the Nevado de Colima. Price per flight, up to 5 passengers.",
    },
    incluye: { es: ["Vuelo de 15 minutos", "Vistas de todo Mazamitla"], en: ["15-minute flight", "Views of all Mazamitla"] },
    duracionMin: 15,
    edadMinima: 4,
    minimoPasajeros: null,
    categoria: "aire",
    adultoCentavos: 1200000,
    ninoCentavos: 0,
    precioNinoConfirmado: true,
    nota: { es: "Precio por vuelo completo de 15 minutos, hasta 5 pasajeros. El vuelo de 30 minutos cuesta $24,000.", en: "Price per full 15-minute flight, up to 5 passengers. The 30-minute flight costs $24,000." },
    imagenes: [
      img("h-img1.webp", "Helicóptero en un campo junto al bosque", "Helicopter in a field by the forest"),
      img("h-img2.webp", "Vista aérea del pueblo y la sierra", "Aerial view of the town and sierra"),
      img("h-img3.webp", "Piloto junto al helicóptero", "Pilot next to the helicopter"),
      img("h-img4.webp", "Vuelo sobre la sierra", "Flight over the sierra"),
      img("h-img5.webp", "Vuelo sobre la sierra", "Flight over the sierra"),
      img("h-img6.webp", "Vuelo sobre la sierra", "Flight over the sierra"),
    ],
    videos: [{ archivo: "/media/v_valle.mp4", poster: "/media/v_valle.jpg", alt: { es: "Valle de la sierra", en: "Sierra valley" } }],
    horarios: ["10:00", "11:00", "12:00", "13:00"],
    recurso: "helicoptero",
  },
  {
    idLegado: 16,
    slug: "city-tour",
    tipo: "tour",
    nombre: { es: "City Tour Mazamitla", en: "Mazamitla City Tour" },
    resumen: { es: "Calles, casonas, fábrica de lácteos y artesanías del Pueblo Mágico.", en: "Streets, old houses, dairy factory and crafts of the Magic Town." },
    descripcion: {
      es: "Recorre las calles de Mazamitla y su ambiente familiar: casonas, paisajes, la tradicional fábrica de lácteos y las artesanías locales.",
      en: "Explore Mazamitla's streets and family atmosphere: old houses, scenery, the traditional dairy factory and local crafts.",
    },
    incluye: { es: ["Recorrido por el centro", "Fábrica de lácteos", "Artesanías locales"], en: ["Downtown ride", "Dairy factory", "Local crafts"] },
    duracionMin: null,
    edadMinima: 4,
    minimoPasajeros: 6,
    categoria: "pueblo",
    adultoCentavos: 40000,
    ninoCentavos: 30000,
    precioNinoConfirmado: true,
    imagenes: [
      img("ct-img3.webp", "Camión del tour frente a la parroquia", "Tour truck in front of the parish church"),
      img("ct-img2.webp", "Camión en una calle empedrada", "Truck on a cobblestone street"),
      img("ct-img1.webp", "Visitantes caminando por el centro", "Visitors walking downtown"),
      img("ct-img4.webp", "Calles y fachadas de un pueblo", "Town streets and facades"),
      img("ct-img5.webp", "Calles y fachadas de un pueblo", "Town streets and facades"),
      img("ct-img6.webp", "Calles y fachadas de un pueblo", "Town streets and facades"),
    ],
    horarios: ["11:00", "13:00", "17:00"],
    recurso: "camion",
  },
  {
    idLegado: 17,
    slug: "mundo-aventura",
    tipo: "tour",
    nombre: { es: "Mundo Aventura", en: "Adventure World" },
    resumen: { es: "Puentes, tirolesa y retos entre los pinos de la sierra.", en: "Bridges, zip line and challenges among the sierra pines." },
    descripcion: {
      es: "Un recorrido de aventura en el bosque con puentes colgantes, tirolesa y retos en altura, guiado y con equipo de seguridad.",
      en: "A forest adventure ride with hanging bridges, zip line and high-ropes challenges, guided and with safety gear.",
    },
    incluye: { es: ["Guía capacitado", "Equipo de seguridad", "Tirolesa y puentes"], en: ["Trained guide", "Safety gear", "Zip line and bridges"] },
    duracionMin: 150,
    edadMinima: 6,
    minimoPasajeros: 10,
    categoria: "camion",
    adultoCentavos: 65000,
    ninoCentavos: 55000,
    precioNinoConfirmado: true,
    imagenes: [
      img("ma-img1.webp", "Parque de aventura entre los árboles", "Adventure park among the trees"),
      img("ma-img2.webp", "Aventura entre los árboles", "Adventure among the trees"),
      img("ma-img3.webp", "Aventura entre los árboles", "Adventure among the trees"),
      img("ma-img4.webp", "Aventura entre los árboles", "Adventure among the trees"),
    ],
    videos: [{ archivo: "/media/v_bosque.mp4", poster: "/media/v_bosque.jpg", alt: { es: "Bosque de la sierra visto desde el aire", en: "Sierra forest from the air" } }],
    horarios: ["10:00", "13:00"],
    recurso: "camion",
  },
  {
    idLegado: 18,
    slug: "amanecer-ranchero",
    tipo: "tour",
    nombre: { es: "Amanecer Ranchero", en: "Ranch Sunrise" },
    resumen: { es: "Sal con el primer sol al rancho: paisaje, caballos y desayuno de campo.", en: "Head to the ranch at first light: scenery, horses and a country breakfast." },
    descripcion: {
      es: "Salida temprano a un rancho de la sierra para ver amanecer, convivir con los caballos y desayunar al aire libre.",
      en: "An early departure to a sierra ranch to watch the sunrise, meet the horses and have breakfast outdoors.",
    },
    incluye: { es: ["Traslado en camión", "Amanecer en el rancho", "Convivencia con caballos"], en: ["Truck transfer", "Sunrise at the ranch", "Time with the horses"] },
    duracionMin: 180,
    edadMinima: 4,
    minimoPasajeros: 10,
    categoria: "camion",
    adultoCentavos: 60000,
    ninoCentavos: 40000,
    precioNinoConfirmado: true,
    imagenes: [
      img("ar-img1.webp", "Caballo al amanecer", "Horse at dawn"),
      img("ar-img2.webp", "Amanecer en la sierra", "Sunrise in the sierra"),
      img("ar-img3.webp", "Amanecer en la sierra", "Sunrise in the sierra"),
      img("pa-img1.webp", "Camino de la sierra", "Sierra road"),
    ],
    videos: [{ archivo: "/media/v_valle.mp4", poster: "/media/v_valle.jpg", alt: { es: "Valle de la sierra", en: "Sierra valley" } }],
    horarios: ["07:00"],
    recurso: "camion",
  },
  {
    idLegado: 19,
    slug: "mazamitla-sierra-tours-express",
    tipo: "combo",
    nombre: { es: "Mazamitla Sierra Tours Express", en: "Mazamitla Sierra Tours Express" },
    resumen: { es: "El día completo en un solo paquete: lo mejor de la sierra y del pueblo.", en: "The full day in one package: the best of the sierra and the town." },
    descripcion: {
      es: "Paquete exprés que junta varias experiencias de Mazamitla en un solo día.",
      en: "An express package that combines several Mazamitla experiences in one day.",
    },
    incluye: { es: ["Varias experiencias en un día", "Guía capacitado", "Traslados"], en: ["Several experiences in one day", "Trained guide", "Transfers"] },
    duracionMin: 480,
    edadMinima: 4,
    minimoPasajeros: 10,
    categoria: "camion",
    adultoCentavos: 225000,
    ninoCentavos: 215000,
    precioNinoConfirmado: true,
    imagenes: [
      img("st-img3.webp", "Camión safari en un camino de terracería de la sierra", "Safari truck on a dirt road in the sierra"),
      img("pa-img1.webp", "Camino de la sierra", "Sierra road"),
      img("cz-img4.webp", "Cascada entre el bosque", "Waterfall in the forest"),
      img("ct-img4.webp", "Calles y fachadas de un pueblo", "Town streets and facades"),
      img("no-img1.webp", "Fogata con amigos de noche", "Campfire with friends at night"),
    ],
    videos: [{ archivo: "/media/v_bosque.mp4", poster: "/media/v_bosque.jpg", alt: { es: "Bosque de la sierra visto desde el aire", en: "Sierra forest from the air" } }],
    horarios: ["09:00"],
    recurso: "camion",
  },
];

export const categorias: Record<TourContenido["categoria"], Texto> = {
  camion: { es: "Camión safari", en: "Safari truck" },
  caballo: { es: "A caballo", en: "Horseback" },
  nocturno: { es: "De noche", en: "Night" },
  aire: { es: "Desde el aire", en: "From the air" },
  pueblo: { es: "Pueblo Mágico", en: "Magic Town" },
};

/** Estacionamiento público donde dejan el coche los clientes del tour. */
export const estacionamiento = { nombre: "STM Estacionamiento Público", direccion: "C. 16 de Septiembre 26A, La Gloria, 49500 Mazamitla, Jal.", mapa: "https://www.google.com/maps/search/?api=1&query=STM+Estacionamiento+P%C3%BAblico+16+de+Septiembre+26A+Mazamitla+Jalisco" };

export const oficina = { direccion: "Hidalgo 1 A, Centro de Mazamitla", mapa: "https://www.google.com/maps/search/?api=1&query=Hidalgo+1+A+Centro+Mazamitla+Jalisco" };

export const contacto = {
  telefonos: ["+52 33 1129 5504", "+52 38 2538 0060", "+52 33 1460 8410"],
  instagram: "mazamitla.sierratour",
  bar: "barde11a11",
  resenasGoogle: { calificacion: 4.5, total: 685, consultado: "2026-11-03" },
};

export const tourPorSlug = (slug: string) => tours.find((t) => t.slug === slug);
